import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, Stack, IconButton, CircularProgress, Button } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import NeonCard from '../components/ui/NeonCard';
import VideoCall from '../components/VideoCall';
// Connect to Pipecatcloud deployed bot
import { DailyPipecatService } from '../services/dailyPipecatService';
import { PlayArrow, Stop } from '@mui/icons-material';
import VoiceProviderSelect from '../components/ui/VoiceProviderSelect';
import RightAnalytics from '../components/clinic/RightAnalytics';
import PainMapPanel from '../components/clinic/PainMapPanel';
import { FileProvider } from '../contexts/FileContext';
import {
  isPainComplaint,
  isPainLocationCorrection,
  isPainLocationRequest,
  isVerbalLocationQuestion,
} from '../utils/painLocationDetection';

function normalizeTranscriptForDedup(text = '') {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCallActive, setIsCallActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [conversationLogs, setConversationLogs] = useState([]);
  const [currentAssistantMessage, setCurrentAssistantMessage] = useState('');
  const [voiceProvider, setVoiceProvider] = useState('');
  const [painCaptureEnabled, setPainCaptureEnabled] = useState(false);
  const [painCaptureSessionKey, setPainCaptureSessionKey] = useState(0);
  const painCaptureEnabledRef = useRef(false);
  
  // Get user data from navigation state
  const emiratesId = location.state?.emiratesId;
  const userName = location.state?.name;
  
  // Log the user data when component mounts
  useEffect(() => {
    if (emiratesId && userName) {
      console.log('🆔 Emirates ID:', emiratesId);
      console.log('👤 Name:', userName);
    }
  }, [emiratesId, userName]);
  
  // Use refs to track message state and prevent race conditions
  const currentMessageRef = useRef('');
  const messageBeingSavedRef = useRef(false);
  const saveTimeoutRef = useRef(null); // Timeout for auto-saving after bot stops
  
  const service = useRef(new DailyPipecatService());
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const audioContextRef = useRef(null);
  const animationFrameRef = useRef(null);
  const endCallRef = useRef(null);
  const painGuidanceExplainedRef = useRef(false);
  const painLocationResolvedRef = useRef(false);
  const lastPainLocationHintAtRef = useRef(0);
  const cameraInitRef = useRef(false);
  const localStreamRef = useRef(null);
  const lastUserTranscriptRef = useRef({ text: '', at: 0 });
  const lastUserTurnSignalRef = useRef({ text: '', at: 0 });
  const conversationScrollRef = useRef(null);
  const pendingPainConfirmRef = useRef(null);
  const painConfirmRetryTimerRef = useRef(null);
  const painConfirmAttemptsRef = useRef(0);
  const botIsSpeakingRef = useRef(false);
  const botSpeakingGenRef = useRef(0);
  const botSpeakingClearTimerRef = useRef(null);
  const pendingUserTurnRef = useRef(null);
  const prevVoiceProviderRef = useRef('');
  const BOT_SPEAKING_HOLDOFF_MS = 500;

  const markBotSpeaking = useCallback(() => {
    botSpeakingGenRef.current += 1;
    if (botSpeakingClearTimerRef.current) {
      clearTimeout(botSpeakingClearTimerRef.current);
      botSpeakingClearTimerRef.current = null;
    }
    botIsSpeakingRef.current = true;
  }, []);

  const flushPendingUserTurn = useCallback(() => {
    const pending = pendingUserTurnRef.current;
    if (!pending?.text || !service.current?.isConnectedToBot()) {
      return;
    }
    const signal = lastUserTurnSignalRef.current;
    const now = Date.now();
    if (
      pending.normalized &&
      (signal.text !== pending.normalized || now - signal.at > 1500)
    ) {
      service.current.sendUserTurnComplete(pending.text);
      lastUserTurnSignalRef.current = { text: pending.normalized, at: now };
    }
    pendingUserTurnRef.current = null;
  }, []);

  const scheduleBotSpeakingClear = useCallback((source) => {
    const genAtSchedule = botSpeakingGenRef.current;
    if (botSpeakingClearTimerRef.current) {
      clearTimeout(botSpeakingClearTimerRef.current);
    }
    botSpeakingClearTimerRef.current = setTimeout(() => {
      if (genAtSchedule !== botSpeakingGenRef.current) {
        return;
      }
      botIsSpeakingRef.current = false;
      botSpeakingClearTimerRef.current = null;
      flushPendingUserTurn();
    }, BOT_SPEAKING_HOLDOFF_MS);
  }, [flushPendingUserTurn]);

  // Helper function to save assistant message
  const saveAssistantMessage = useCallback((text) => {
    if (!text || text.trim().length === 0 || messageBeingSavedRef.current) {
      return;
    }
    
    messageBeingSavedRef.current = true;
    const now = Date.now();
    
    setConversationLogs(prev => {
      const trimmed = text.trim();
      const latest = prev[0];
      if (latest?.role === 'assistant' && latest.text?.trim() === trimmed) {
        return prev;
      }
      // Create new message with unique ID and timestamp
      const newMessage = {
        id: `assistant-${now}`,
        role: 'assistant',
        text: trimmed,
        timestamp: new Date(now).toLocaleTimeString(),
        timestampMs: now
      };
      return [newMessage, ...prev];
    });

    if (isPainLocationRequest(text)) {
      setPainCaptureEnabled(true);
      setPainCaptureSessionKey(Date.now());
      if (!painGuidanceExplainedRef.current && service.current?.isConnectedToBot()) {
        service.current.sendPainLocationGuidanceHint();
        painGuidanceExplainedRef.current = true;
      }
    }
    
    // Reset refs
    currentMessageRef.current = '';
    setCurrentAssistantMessage('');
    messageBeingSavedRef.current = false;
  }, []);

  const handlePainCapturePrompt = useCallback((stage) => {
    if (!service.current || !service.current.isConnectedToBot()) return;
    service.current.sendPainLocationTimeout(stage);
  }, []);

  const clearPainConfirmRetry = useCallback(() => {
    if (painConfirmRetryTimerRef.current) {
      clearTimeout(painConfirmRetryTimerRef.current);
      painConfirmRetryTimerRef.current = null;
    }
  }, []);

  const clearConversationState = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    currentMessageRef.current = '';
    messageBeingSavedRef.current = false;
    setCurrentAssistantMessage('');
    setConversationLogs([]);
    setPainCaptureEnabled(false);
    setPainCaptureSessionKey(Date.now());
    painGuidanceExplainedRef.current = false;
    painLocationResolvedRef.current = false;
    lastPainLocationHintAtRef.current = 0;
    clearPainConfirmRetry();
    pendingPainConfirmRef.current = null;
    painConfirmAttemptsRef.current = 0;
    pendingUserTurnRef.current = null;
  }, [clearPainConfirmRetry]);

  useEffect(() => {
    const previous = prevVoiceProviderRef.current;
    if (previous && voiceProvider && previous !== voiceProvider) {
      clearConversationState();
    }
    prevVoiceProviderRef.current = voiceProvider;
  }, [voiceProvider, clearConversationState]);

  const schedulePainConfirmRetry = useCallback(() => {
    clearPainConfirmRetry();
    painConfirmRetryTimerRef.current = setTimeout(() => {
      if (!pendingPainConfirmRef.current || !service.current) {
        return;
      }
      if (painConfirmAttemptsRef.current >= 2) {
        pendingPainConfirmRef.current = null;
        return;
      }
      painConfirmAttemptsRef.current += 1;
      console.warn('⚠️ No bot reaction after pain confirm, retrying...', {
        attempt: painConfirmAttemptsRef.current,
      });
      service.current.sendPainLocationSelection(pendingPainConfirmRef.current);
      schedulePainConfirmRetry();
    }, 3200);
  }, [clearPainConfirmRetry]);

  const handlePainLocationConfirmed = useCallback((payload) => {
    if (!service.current) return;
    const confirmPayload = {
      ...payload,
      client_confirm_id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };
    pendingPainConfirmRef.current = confirmPayload;
    painConfirmAttemptsRef.current = 1;
    service.current.sendPainLocationSelection(confirmPayload);
    schedulePainConfirmRetry();
    painLocationResolvedRef.current = true;
    lastPainLocationHintAtRef.current = 0;
    setPainCaptureEnabled(false);
  }, [schedulePainConfirmRetry]);

  const handlePainCaptureExpired = useCallback(() => {
    setPainCaptureEnabled(false);
  }, []);

  useEffect(() => {
    painCaptureEnabledRef.current = painCaptureEnabled;
  }, [painCaptureEnabled]);

  // Handle conversation updates from the bot
  const handleConversationUpdate = useCallback((message) => {
    // Handle different types of messages from the bot
    if (message.type === 'user-transcription') {
      // Clear any pending auto-save timeout; bot message is saved on bot-stopped-speaking
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      const now = Date.now();
      const userText = message.data?.text || message.text || 'User spoke';
      const normalized = (userText || '').trim().toLowerCase();
      const normalizedForDedup = normalizeTranscriptForDedup(userText);

      if (normalized) {
        const recent = lastUserTranscriptRef.current;
        if (
          recent.text === normalized &&
          now - recent.at < 2500
        ) {
          return;
        }
        lastUserTranscriptRef.current = { text: normalized, at: now };
      }

      setConversationLogs(prev => {
        const trimmed = userText.trim();
        const latestUser = prev.find((entry) => entry.role === 'user');
        if (latestUser) {
          const latestUserText = (latestUser.text || '').trim();
          const latestNorm = normalizeTranscriptForDedup(latestUserText);
          if (latestUserText === trimmed || (normalizedForDedup && latestNorm === normalizedForDedup)) {
            return prev;
          }
          const isGrowingInterim =
            latestUser.id === prev[0]?.id &&
            trimmed.length > latestUserText.length &&
            trimmed.toLowerCase().startsWith(latestUserText.toLowerCase()) &&
            now - (latestUser.timestampMs || 0) < 3500;
          if (isGrowingInterim) {
            return [
              {
                ...latestUser,
                text: trimmed,
                timestamp: new Date(now).toLocaleTimeString(),
                timestampMs: now,
              },
              ...prev.slice(1),
            ];
          }
        }

        const repeatedRecentUser = prev
          .filter((entry) => entry.role === 'user')
          .slice(0, 4)
          .some(
            (entry) =>
              normalizeTranscriptForDedup(entry.text || '') === normalizedForDedup &&
              now - (entry.timestampMs || 0) < 15000
          );
        if (repeatedRecentUser) {
          return prev;
        }

        const newMessage = {
          id: `user-${now}`,
          role: 'user',
          text: trimmed || 'User spoke',
          timestamp: new Date(now).toLocaleTimeString(),
          timestampMs: now
        };
        return [newMessage, ...prev];
      });

      if (service.current?.isConnectedToBot()) {
        const trimmedText = userText.trim();
        if (botIsSpeakingRef.current) {
          if (normalizedForDedup) {
            pendingUserTurnRef.current = {
              text: trimmedText,
              normalized: normalizedForDedup,
              at: now,
            };
          }
        } else {
          const signal = lastUserTurnSignalRef.current;
          if (
            normalizedForDedup &&
            (signal.text !== normalizedForDedup || now - signal.at > 1500)
          ) {
            service.current.sendUserTurnComplete(trimmedText);
            lastUserTurnSignalRef.current = { text: normalizedForDedup, at: now };
          }
        }
      }

      if (
        !painLocationResolvedRef.current &&
        !painCaptureEnabledRef.current &&
        isPainComplaint(userText) &&
        service.current?.isConnectedToBot()
      ) {
        const nowMs = Date.now();
        if (nowMs - lastPainLocationHintAtRef.current > 6000) {
          service.current.sendPainLocationRequestHint();
          lastPainLocationHintAtRef.current = nowMs;
        }
      }

      if (
        painLocationResolvedRef.current &&
        !painCaptureEnabledRef.current &&
        isPainLocationCorrection(userText)
      ) {
        setPainCaptureEnabled(true);
        setPainCaptureSessionKey(Date.now());
        painLocationResolvedRef.current = false;
        if (service.current?.isConnectedToBot()) {
          service.current.sendPainLocationRetryHint();
        }
      }
    } else if (message.type === 'bot-tts-text') {
      markBotSpeaking();
      if (pendingPainConfirmRef.current) {
        pendingPainConfirmRef.current = null;
        painConfirmAttemptsRef.current = 0;
        clearPainConfirmRetry();
      }
      // Bot is generating spoken text (TTS only - ignore bot-llm-text which contains thinking)
      const textContent = message.data?.text || '';
      if (
        textContent &&
        !painCaptureEnabledRef.current &&
        !painLocationResolvedRef.current &&
        isVerbalLocationQuestion(textContent) &&
        service.current?.isConnectedToBot()
      ) {
        const nowMs = Date.now();
        if (nowMs - lastPainLocationHintAtRef.current > 4000) {
          service.current.sendPainLocationRequestHint();
          lastPainLocationHintAtRef.current = nowMs;
        }
      }
      
      // Accumulate text in both state and ref
      currentMessageRef.current += textContent;
      setCurrentAssistantMessage(prev => prev + textContent);
      
      // Clear any pending save timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      
      // Set a timeout to auto-save after 2 seconds of bot silence
      saveTimeoutRef.current = setTimeout(() => {
        if (currentMessageRef.current.trim() && !messageBeingSavedRef.current) {
          saveAssistantMessage(currentMessageRef.current);
        }
        saveTimeoutRef.current = null;
      }, 2000);
    } else if (message.type === 'bot-llm-stopped') {
      scheduleBotSpeakingClear('bot-llm-stopped');
      flushPendingUserTurn();
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      if (currentMessageRef.current.trim() && !messageBeingSavedRef.current) {
        saveAssistantMessage(currentMessageRef.current);
      }
    } else if (
      message.type === 'bot-stopped-speaking' ||
      message.type === 'bot-tts-stopped'
    ) {
      scheduleBotSpeakingClear(message.type);
      flushPendingUserTurn();
    } else if (
      message.type === 'bot-started-speaking' ||
      message.type === 'bot-tts-started' ||
      message.type === 'bot-llm-started'
    ) {
      markBotSpeaking();
    } else if (message.type === 'user-started-speaking' || message.type === 'user-stopped-speaking') {
      // User speech events - silent
    } else if (message.type === 'metrics' || message.type === 'user-llm-text') {
      // Metrics and other events we don't need to display - silent
    }
  }, [saveAssistantMessage, clearPainConfirmRetry, markBotSpeaking, scheduleBotSpeakingClear, flushPendingUserTurn]);

  useEffect(() => {
    if (conversationScrollRef.current) {
      conversationScrollRef.current.scrollTop = 0;
    }
  }, [conversationLogs, currentAssistantMessage]);

  useEffect(() => () => {
    clearPainConfirmRetry();
    if (botSpeakingClearTimerRef.current) {
      clearTimeout(botSpeakingClearTimerRef.current);
    }
  }, [clearPainConfirmRetry]);

  // Initialize local camera once (even in StrictMode dev re-mounting)
  useEffect(() => {
    if (cameraInitRef.current) {
      return undefined;
    }
    cameraInitRef.current = true;

    const initializeCamera = async () => {
      try {
        console.log('🎥 Initializing local camera on Dashboard mount...');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { 
            width: { ideal: 640 }, 
            height: { ideal: 480 },
            frameRate: { ideal: 30 }
          },
          audio: { 
            sampleRate: { ideal: 16000 },
            channelCount: { ideal: 1 },
            echoCancellation: true,
            noiseSuppression: true
          }
        });
        console.log('✅ Local camera initialized:', stream);
        localStreamRef.current = stream;
        setLocalStream(stream);
      } catch (error) {
        console.error('❌ Failed to initialize camera:', error);
      }
    };

    initializeCamera();

    return () => {
      // Cleanup on unmount
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
      cameraInitRef.current = false;
    };
  }, []);

  useEffect(() => {
    const currentService = service.current;
    
    // Set up conversation update handler
    currentService.setOnConversationUpdate((message) => {
      handleConversationUpdate(message);
    });

    // HeyGen lip-synced A/V — video element plays both tracks from one stream
    currentService.setOnRemoteStream((stream) => {
      console.log('🎥 Received remote A/V stream in Dashboard:', stream);
      setRemoteStream(stream);
    });

    currentService.setOnSessionEnded(() => {
      console.log('👋 Session ended by AI — returning to Start Conversation');
      endCallRef.current?.();
    });

    return () => {
      currentService.endCall();
      
      // Clear any pending save timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, [handleConversationUpdate]);

  const startCall = async () => {
    if (!voiceProvider) return;
    try {
      clearConversationState();
      // Immediately show loading state
      setIsConnecting(true);
      painGuidanceExplainedRef.current = false;
      painLocationResolvedRef.current = false;
      lastPainLocationHintAtRef.current = 0;
      
      // Set patient info before starting the call
      if (userName && emiratesId) {
        service.current.setPatientInfo(userName, emiratesId);
      }
      service.current.setVoiceProvider(voiceProvider);
      
      // Start the call - local camera is already running from initial mount
      await service.current.startCall();
      service.current.setAudioMuted(false);
      service.current.setVideoMuted(false);
      
      // Only set active if the service confirms the call is running
      setIsCallActive(service.current.isCallInProgress());
      setIsConnecting(false);
      
    } catch (error) {
      console.error('❌ Failed to start call:', error);
      // Clean up properly on error
      await service.current.endCall();
      setIsCallActive(false);
      setIsConnecting(false);
    }
  };

  const endCall = () => {
    setIsCallActive(false);
    clearConversationState();

    setRemoteStream(null);
    service.current.endCall();

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
  };
  endCallRef.current = endCall;

  const handleLogout = () => {
    // Stop any active call
    if (isCallActive) {
      endCall();
    }
    // Navigate back to landing page
    navigate('/');
  };

  const callActionButtonSx = {
    minWidth: 44,
    minHeight: 44,
    textTransform: 'none',
    borderColor: 'rgba(50,175,252,0.45)',
    color: '#dce7f4',
    borderRadius: '8px',
    backgroundColor: 'rgba(6, 18, 40, 0.78)',
    fontFamily: 'Dubai, sans-serif',
    fontWeight: 500,
    textShadow: '0 1px 1px rgba(0,0,0,0.45)',
    backdropFilter: 'blur(2px)',
    '&:hover': {
      backgroundColor: 'rgba(50, 175, 252, 0.18)',
      borderColor: '#32affc',
    },
    '& .MuiSvgIcon-root': { color: '#dce7f4' },
    '&.Mui-disabled': {
      color: 'rgba(220, 231, 244, 0.75)',
      borderColor: 'rgba(50, 175, 252, 0.28)',
      backgroundColor: 'rgba(6, 18, 40, 0.6)',
    },
  };

  const consultantControlSx = {
    fontFamily: 'Dubai, sans-serif',
    fontWeight: 500,
    fontSize: { xs: '14px', sm: '15px' },
    textTransform: 'none',
    color: '#dce7f4',
    backgroundColor: 'transparent',
    border: '1px solid rgba(50, 175, 252, 0.45)',
    borderRadius: '8px',
    minWidth: 44,
    minHeight: 44,
    px: 2,
    py: 0.75,
    '&:hover': {
      backgroundColor: 'rgba(50, 175, 252, 0.08)',
      borderColor: '#32affc',
    },
    '&.Mui-disabled': {
      color: 'rgba(227, 235, 241, 0.45)',
      borderColor: 'rgba(50, 175, 252, 0.2)',
      backgroundColor: 'transparent',
    },
  };

  const consultantDropdownHighlightSx = {
    ...consultantControlSx,
    borderColor: '#8fd4ff',
    color: '#eef8ff',
    backgroundColor: 'rgba(44, 132, 220, 0.28)',
    boxShadow: '0 0 0 1px rgba(143, 212, 255, 0.58), 0 0 30px rgba(76, 176, 255, 0.56)',
    '&:hover': {
      backgroundColor: 'rgba(58, 152, 238, 0.36)',
      borderColor: '#b4e4ff',
      color: '#ffffff',
      boxShadow: '0 0 0 1px rgba(180, 228, 255, 0.7), 0 0 36px rgba(90, 190, 255, 0.7)',
    },
  };

  return (
    <FileProvider emiratesId={emiratesId}>
      <Box sx={{ 
        flexGrow: 1, 
        width: '100vw',
        height: '100vh',
        minHeight: '100vh',
        p: 0,
        display: "flex", 
        alignItems: { xs: 'stretch', md: 'center' }, 
        justifyContent: { xs: 'flex-start', md: 'center' }, 
        position: 'relative',
        overflow: { xs: 'auto', md: 'hidden' },
      }}>
      {/* Logout Button - Fixed at top right */}
      <IconButton
        onClick={handleLogout}
        sx={{
          position: 'fixed',
          top: { xs: 10, sm: 10, md: 'calc(3.5vh + 12px)' },
          right: { xs: 10, sm: 10, md: 'calc(2.5vw - 22px)' },
          width: { xs: 38, sm: 38, md: 40 },
          height: { xs: 36, sm: 36, md: 42 },
          borderRadius: '10px',
          border: '1px solid rgba(66, 157, 245, 0.38)',
          backgroundColor: 'transparent',
          color: '#e5f2ff',
          padding: 0,
          zIndex: 1000,
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'transparent',
            borderColor: 'rgba(102, 185, 255, 0.55)',
            boxShadow: 'none',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Box
          component="span"
          sx={{
            width: { xs: 18, sm: 18, md: 22 },
            height: { xs: 18, sm: 18, md: 22 },
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'inherit',
          }}
        >
          <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true" focusable="false">
            <path
              fill="currentColor"
              d="M12 22a2 2 0 0 1-2-2V10.83l-2.59 2.58a2 2 0 1 1-2.82-2.82l6-6a2 2 0 0 1 2.82 0l6 6a2 2 0 1 1-2.82 2.82L14 10.83V20a2 2 0 0 1-2 2z"
            />
          </svg>
        </Box>
      </IconButton>
      
      <Box sx={{ 
        width: { xs: '94vw', md: '85vw', lg: '85vw' },
        maxWidth: { xs: '94vw', md: '85vw', lg: '85vw' },
        maxHeight: { xs: 'none', md: '90vh' },
        display: { xs: 'flex', md: 'grid' },
        flexDirection: { xs: 'column' },
        gap: { xs: 2, sm: 2, md: 3 },
        gridTemplateColumns: { md: '8fr 4fr', lg: '8fr 4fr', xl: '7fr 3fr' },
        gridTemplateRows: { md: '65fr 25fr' },
        height: { md: '90vh' },
        pt: { xs: 7.5, md: 0 },
        mx: 'auto',
        minHeight: 0,
        overflow: { xs: 'auto', md: 'hidden' },
        boxSizing: 'border-box',
      }}>
        {/* 1. Start Conversation / Video Call Area */}
        <Box sx={{ 
          order: { xs: 1, md: 'unset' },
          gridColumn: { md: 1 },
          gridRow: { md: 1 },
          minHeight: { xs: '40vh', md: 0 },
          height: { xs: 'auto', md: '100%' },
        }}>
              <NeonCard sx={{ 
                height: { xs: 'auto', md: '100%' }, 
                minHeight: { xs: '40vh', md: '100%' },
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center', 
                alignItems: 'center', 
                p: { xs: "10px", sm: "10px" },
                position: 'relative',
                overflow: 'visible',
              }}>
                {!isCallActive && !isConnecting ? (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: { xs: '90%', sm: 460, md: 460 },
                      px: { xs: 1, sm: 2 },
                      py: { xs: 1.25, sm: 1.5 },
                      border: voiceProvider
                        ? '1px solid rgba(96, 176, 245, 0.34)'
                        : '1px solid rgba(96, 176, 245, 0.08)',
                      borderRadius: '12px',
                      background: voiceProvider
                        ? 'rgba(6, 22, 52, 0.22)'
                        : 'rgba(6, 22, 52, 0.04)',
                      boxShadow: voiceProvider
                        ? '0 0 0 1px rgba(64, 152, 230, 0.14), 0 0 22px rgba(26, 112, 190, 0.18)'
                        : 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: { xs: 1, sm: 1.25 },
                      transition: 'border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#dce7f4',
                          fontFamily: 'Dubai, sans-serif',
                          fontWeight: 600,
                          fontSize: { xs: '15px', sm: '16px' },
                          opacity: voiceProvider ? 1 : 0.2,
                          transition: 'opacity 0.3s ease',
                        }}
                      >
                        Choose your Healthcare Consultant
                      </Typography>
                      <VoiceProviderSelect
                        value={voiceProvider}
                        onChange={setVoiceProvider}
                        disabled={isConnecting || isCallActive}
                        placeholder="Consultant"
                        buttonSx={voiceProvider ? consultantControlSx : consultantDropdownHighlightSx}
                        sx={{ zIndex: 2, flexShrink: 0 }}
                      />
                    </Box>
                    <Button
                      onClick={startCall}
                      variant="outlined"
                      size="large"
                      disabled={isConnecting || !voiceProvider}
                      startIcon={isConnecting ? <CircularProgress size={16} sx={{ color: '#dce7f4' }} /> : <PlayArrow />}
                      sx={{
                        ...consultantControlSx,
                        width: '100%',
                        justifyContent: 'center',
                        opacity: voiceProvider ? 1 : 0.2,
                        transition: 'opacity 0.3s ease',
                        borderColor: voiceProvider ? '#8fd4ff' : consultantControlSx.border,
                        color: voiceProvider ? '#eef8ff' : consultantControlSx.color,
                        backgroundColor: voiceProvider ? 'rgba(44, 132, 220, 0.28)' : consultantControlSx.backgroundColor,
                        boxShadow: voiceProvider ? '0 0 0 1px rgba(143, 212, 255, 0.58), 0 0 30px rgba(76, 176, 255, 0.56)' : 'none',
                        '&:hover': voiceProvider
                          ? {
                              backgroundColor: 'rgba(58, 152, 238, 0.36)',
                              borderColor: '#b4e4ff',
                              color: '#ffffff',
                              boxShadow: '0 0 0 1px rgba(180, 228, 255, 0.7), 0 0 36px rgba(90, 190, 255, 0.7)',
                            }
                          : consultantControlSx['&:hover'],
                        '& .MuiButton-startIcon, & .MuiSvgIcon-root': {
                          color: voiceProvider ? '#eef8ff' : '#dce7f4',
                        },
                      }}
                    >
                      {isConnecting ? 'Connecting...' : 'Start the Conversation'}
                    </Button>
                  </Box>
                ) : isConnecting ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      color: '#dce7f4',
                    }}
                  >
                    <CircularProgress size={22} sx={{ color: '#dce7f4' }} />
                    <Typography sx={{ fontFamily: 'Dubai, sans-serif', fontSize: { xs: '14px', sm: '15px' } }}>
                      Connecting...
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', width: '100%', height: '100%' }}>
                    <Button
                        onClick={endCall}
                        variant="outlined"
                        size="large"
                        startIcon={<Stop />}
                        sx={{ 
                          ...callActionButtonSx,
                          position: 'absolute',
                          top: { xs: 10, sm: 14 },
                          left: { xs: 10, sm: 14 },
                          zIndex: 3,
                          fontSize: { xs: '14px', sm: '15px' },
                          px: 2,
                          py: 0.75,
                        }}
                      >
                        Stop Call
                      </Button>
                    
                    {/* HeyGen Avatar Video */}
                    <Box sx={{ width: "100%", height: { xs: '35vh', md: '100%' }, position: 'relative' }}>
                      <VideoCall
                        ref={remoteVideoRef}
                        stream={remoteStream}
                        isRemote={true}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  </Box>
                )}
              </NeonCard>
            </Box>

        {/* 2–3. Camera + action buttons */}
        <Box sx={{
          order: { xs: 2, md: 'unset' },
          gridColumn: { md: 1 },
          gridRow: { md: 2 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 2, md: 3 },
          minHeight: { xs: 'auto', md: 0 },
          height: { xs: 'auto', md: '100%' },
        }}>
            {/* 2. Patient Camera */}
            <Box sx={{ flex: { xs: 'none', sm: 1 }, minHeight: { xs: '25vh', md: 0 }, height: { md: '100%' } }}>
              <NeonCard sx={{ 
                height: '100%', 
                position: 'relative', 
                overflow: 'hidden',
                p: 0,  // Remove default padding to fill container
                '& > div': {  // Target the inner Box wrapper in NeonCard
                  height: '100%',
                  width: '100%'
                }
              }}>
                <VideoCall
                  ref={localVideoRef}
                  stream={localStream}
                  isRemote={false}
                  manageOwnLocalStream={false}
                  pipecatService={service.current}
                  sx={{ 
                    width: '100%', 
                    height: '100%',
                    borderRadius: 0  // Remove border radius to fill edge-to-edge
                  }}
                />
              </NeonCard>
            </Box>

            {/* 3. Medical / Hospitals / Upload */}
            <Box
              sx={{
                flex: { xs: 'none', sm: 1 },
                minHeight: { xs: 'auto', md: 0 },
                height: { md: '100%' },
                display: 'flex',
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: { xs: 'flex-start', md: 'center' },
              }}
            >
              <NeonCard sx={{
                display: 'contents',
                p: 0,
                background: 'transparent',
                boxShadow: 'none',
                '&::before': { display: 'none' },
                '&::after': { display: 'none' },
              }}>
                <Box
                  sx={{
                    textAlign: 'center',
                    width: '100%',
                    height: { xs: 'auto', md: '100%' },
                    display: 'flex',
                    alignItems: { xs: 'flex-start', md: 'center' },
                  }}
                >
                  <RightAnalytics 
                    pipecatService={service.current} 
                    emiratesId={emiratesId}
                    patientName={userName}
                  />
                </Box>
              </NeonCard>
            </Box>
        </Box>

        <PainMapPanel
          emiratesId={emiratesId}
          patientName={userName}
          painCaptureEnabled={painCaptureEnabled}
          painCaptureSessionKey={painCaptureSessionKey}
          onPainLocationConfirmed={handlePainLocationConfirmed}
          onPainCapturePrompt={handlePainCapturePrompt}
          onPainCaptureExpired={handlePainCaptureExpired}
          sx={{ 
          order: { xs: 5, md: 'unset' },
          gridColumn: { md: 2 },
          gridRow: { md: 1 },
          height: { xs: '72vh', md: '100%' },
          minHeight: { xs: '560px', md: 0 },
          boxShadow: 'none',
          '&::before': { display: 'none' },
          '&::after': { display: 'none' },
        }} />
        <NeonCard sx={{ 
          order: { xs: 4, md: 'unset' },
          gridColumn: { md: 2 },
          gridRow: { md: 2 },
          position: 'relative',
          height: { xs: '28vh', md: '100%' },
          minHeight: { xs: '180px', md: 0 },
          background: { xs: 'rgba(6, 18, 40, 0.56)', md: undefined },
          border: { xs: '1px solid rgba(50,175,252,0.32)', md: undefined },
          overflow: 'hidden',
          maxWidth: '100%',
          p: 0,
          display: 'flex',
          flexDirection: 'column',
        }}>
            <Box
              ref={conversationScrollRef}
              sx={{ 
              flex: 1,
              minHeight: 0,
              overflowY: 'auto', 
              overflowX: 'hidden',
              p: { xs: 1, sm: 2 },
              boxSizing: 'border-box',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.2)',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(120,154,181,0.5)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(120,154,181,0.7)',
                }
              }
            }}>
              <Stack spacing={{ xs: 0.35, sm: 0.5 }} sx={{ width: '100%' }}>
                {!currentAssistantMessage && conversationLogs.length === 0 && (
                  <Box
                    sx={{
                      p: { xs: 0.3, sm: 0.5 },
                      opacity: 0.7,
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#9dc6eb',
                        fontSize: { xs: '0.78rem', sm: '0.86rem' },
                        fontFamily: 'Dubai',
                      }}
                    >
                      Conversation will appear here.
                    </Typography>
                  </Box>
                )}
                {/* Show current assistant message being built */}
                {currentAssistantMessage && (
                <Box
                  sx={{
                    p: { xs: 0.2, sm: 0.35 },
                    opacity: 0.8,
                    width: '100%',
                    boxSizing: 'border-box',
                    maxWidth: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#fff',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                      maxWidth: '100%',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 500, color: '#32affc' }}>
                      AI Doctor:
                    </Box>{' '}
                    {currentAssistantMessage}
                  </Typography>
                </Box>
                )}

                {conversationLogs.map((log) => (
                  <Box
                    key={log.id}
                    sx={{
                      p: { xs: 0.2, sm: 0.35 },
                      width: '100%',
                      boxSizing: 'border-box',
                      maxWidth: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#fff',
                        fontFamily: 'Dubai',
                        fontWeight: '300',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        maxWidth: '100%',
                        fontSize: { xs: '0.8rem', sm: '12pt' },
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 500, color: log.role === 'user' ? '#a5ffbc' : '#32affc' }}>
                        {log.role === 'user' ? 'You:' : 'AI Doctor:'}
                      </Box>{' '}
                      {log.text}
                    </Typography>
                  </Box>
                  ))}
              </Stack>
            </Box>
        </NeonCard>
      </Box>

      {/* Add CSS animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
      </Box>
    </FileProvider>
  );
}
